import { useState } from 'react'
import { listProblemReportAssignees } from '../../../api/generated/problem-reports/problem-reports'
import type { ProblemReportAdminRead } from '../../../api/generated/model/problemReportAdminRead'
import type { ProblemReportCommand } from '../../../api/generated/model/problemReportCommand'
import { Button } from '../../../components/ui/button'
import { useT } from '../../../i18n'
import { reportCopy } from '../copy'
import { reportInputClass } from '../ReportForm'
import { useReportRead } from '../own-data'
import { adminCopy } from './copy'
import type { createAdminCommands } from './commands'
const graph = {
  received: ['investigating', 'closed'],
  investigating: ['planned', 'fixed', 'closed'],
  planned: ['investigating', 'fixed', 'closed'],
  fixed: ['investigating'],
  closed: [],
} as const
export default function AdminActions({
  report,
  commands,
  disabled,
}: {
  report: ProblemReportAdminRead
  commands: ReturnType<typeof createAdminCommands>
  disabled: boolean
}) {
  const t = useT(reportCopy),
    a = useT(adminCopy)
  const [target, setTarget] = useState(''),
    [note, setNote] = useState(''),
    [build, setBuild] = useState(''),
    [evidence, setEvidence] = useState(''),
    [ack, setAck] = useState(false),
    [reason, setReason] = useState(''),
    [invalid, setInvalid] = useState(false)
  const targets: readonly string[] = graph[report.status]
  const validTarget = targets.includes(target) ? target : ''
  const submit = () => {
    const count = [...note.trim()].length
    const needsNote =
      validTarget === 'fixed' ||
      validTarget === 'closed' ||
      report.status === 'fixed'
    if (
      !validTarget ||
      (needsNote && (count < 10 || count > 5000)) ||
      (validTarget === 'fixed' &&
        (!ack ||
          !build.trim() ||
          [...build.trim()].length > 100 ||
          !evidence.trim() ||
          [...evidence.trim()].length > 500)) ||
      (validTarget === 'closed' && !reason)
    ) {
      setInvalid(true)
      return
    }
    setInvalid(false)
    void commands.send(report.id, report.revision, {
      action: 'transition',
      target_status: validTarget as ProblemReportCommand['target_status'],
      ...(needsNote ? { public_note: note.trim() } : {}),
      ...(validTarget === 'fixed'
        ? { fixed_release: build.trim(), release_evidence: evidence.trim() }
        : {}),
      ...(validTarget === 'closed'
        ? { closure_reason: reason as ProblemReportCommand['closure_reason'] }
        : {}),
    })
  }
  return (
    <section className="space-y-3 border-t border-[var(--color-border)] pt-4">
      <h4 className="font-medium">{a.internal}</h4>
      <AssigneePicker report={report} commands={commands} disabled={disabled} />
      <form
        onSubmit={(e) => {
          e.preventDefault()
          submit()
        }}
        className="space-y-3"
      >
        <label className="block space-y-1">
          {a.transition}
          <select
            aria-label={a.transition}
            className={reportInputClass}
            value={validTarget}
            disabled={disabled}
            onChange={(e) => {
              setTarget(e.target.value)
              setAck(false)
            }}
          >
            <option value="">{a.chooseTransition}</option>
            {targets.map((value) => (
              <option key={value} value={value}>
                {t[value as keyof typeof t]}
              </option>
            ))}
          </select>
        </label>
        {(validTarget === 'fixed' ||
          validTarget === 'closed' ||
          report.status === 'fixed') && (
          <label className="block space-y-1">
            {a.publicNote}
            <textarea
              aria-label={a.publicNote}
              value={note}
              disabled={disabled}
              className={reportInputClass}
              onChange={(e) => setNote(e.target.value)}
            />
            <span className="text-xs text-[var(--color-text-muted)]">
              {[...note.trim()].length} / 5,000
            </span>
          </label>
        )}
        {validTarget === 'fixed' && (
          <div className="space-y-3">
            <p>
              {a.environment}: <strong>{report.environment}</strong>
            </p>
            <label className="block space-y-1">
              {a.build}
              <input
                aria-label={a.build}
                className={reportInputClass}
                value={build}
                disabled={disabled}
                onChange={(e) => {
                  setBuild(e.target.value)
                  setAck(false)
                }}
              />
            </label>
            <label className="block space-y-1">
              {a.evidence}
              <input
                aria-label={a.evidence}
                className={reportInputClass}
                value={evidence}
                disabled={disabled}
                onChange={(e) => {
                  setEvidence(e.target.value)
                  setAck(false)
                }}
              />
            </label>
            <label className="flex items-start gap-2">
              <input
                type="checkbox"
                checked={ack}
                disabled={disabled}
                onChange={(e) => setAck(e.target.checked)}
                className="mt-1"
              />
              <span>
                {a.acknowledge} <strong>{report.environment}</strong>
              </span>
            </label>
          </div>
        )}
        {validTarget === 'closed' && (
          <label className="block space-y-1">
            {a.closure}
            <select
              aria-label={a.closure}
              className={reportInputClass}
              value={reason}
              disabled={disabled}
              onChange={(e) => setReason(e.target.value)}
            >
              <option value="">—</option>
              {(
                [
                  'expected_behavior',
                  'not_planned',
                  'cannot_reproduce',
                  'out_of_scope',
                ] as const
              ).map((value) => (
                <option key={value} value={value}>
                  {t[value]}
                </option>
              ))}
            </select>
          </label>
        )}
        {invalid && <p role="alert">{a.required}</p>}
        <Button type="submit" disabled={disabled}>
          {a.save}
        </Button>
      </form>
    </section>
  )
}
function AssigneePicker({
  report,
  commands,
  disabled,
}: {
  report: ProblemReportAdminRead
  commands: ReturnType<typeof createAdminCommands>
  disabled: boolean
}) {
  const t = useT(reportCopy),
    a = useT(adminCopy)
  const [cursor, setCursor] = useState<string>(),
    [selected, setSelected] = useState('')
  const query = useReportRead(['admin', 'assignees', cursor], (options) =>
    listProblemReportAssignees({ cursor, limit: 20 }, options),
  )
  if (query.isPending) return <p role="status">{t.loading}</p>
  if (query.isError || query.data?.status !== 200)
    return (
      <div role="alert">
        {t.error}
        <Button variant="outline" onClick={() => void query.refetch()}>
          {t.retry}
        </Button>
      </div>
    )
  const page = query.data.data
  const eligible = page.items.some(
    (person) => person.membership_id === selected,
  )
  const currentAssignee = page.items.find(
    (person) => person.membership_id === report.assignee_membership_id,
  )
  return (
    <div className="space-y-2">
      <label className="block space-y-1">
        {a.assignee}
        <select
          aria-label={a.assignee}
          className={reportInputClass}
          value={eligible ? selected : ''}
          disabled={disabled || query.isFetching}
          onChange={(e) => setSelected(e.target.value)}
        >
          <option value="">{a.selectAssignee}</option>
          {page.items.map((person) => (
            <option key={person.membership_id} value={person.membership_id}>
              {person.display_name}
            </option>
          ))}
        </select>
      </label>
      {report.assignee_membership_id && (
        <p className="break-all text-xs">
          {a.currentAssignee}: {currentAssignee?.display_name ?? report.assignee_membership_id}
          {!currentAssignee && <> · {a.assigneeOutsidePage}</>}
        </p>
      )}
      {!page.items.length && <p>{a.noAssignees}</p>}
      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          disabled={disabled || query.isFetching || !eligible}
          onClick={() =>
            void commands.send(report.id, report.revision, {
              action: 'assign',
              assignee_membership_id: selected,
            })
          }
        >
          {a.assign}
        </Button>
        {cursor && (
          <Button variant="ghost" onClick={() => setCursor(undefined)}>
            {a.first}
          </Button>
        )}
        {page.has_more && page.next_cursor && (
          <Button
            variant="ghost"
            onClick={() => {
              setCursor(page.next_cursor!)
              setSelected('')
            }}
          >
            {a.next}
          </Button>
        )}
      </div>
    </div>
  )
}
